import React, { useState } from "react";

import { Eye16Regular, EyeOff16Regular, Link20Filled } from "@fluentui/react-icons";
import { Button, Caption1, Field, Input, Spinner } from "@fluentui/react-components";

import { useSelector } from "react-redux";
import { fetchData } from "../../../store/asyncThunks";
import { openToast } from "../../../../toast/store";
import { selectIsErrorValidatingToken, selectIsLoading, selectToken, setAutoUpdate, setToken } from "../../../store";
import { useAppDispatch } from "../../../../../store";
import { ProcessedData } from "../../../store/types";
import { selectIsConnected, setConnected, setDisconnected } from "../../../../../taskpane/components/statusBar/store";

import usePersonalAccessTokenStyles from "../personalAccessToken.styles";

import { initializeCicada, resetDataProducerButtonsText, validateToken } from "../utils";

import DropdownEnvironmentOptions from "./DropdownEnvironmentOptions";
import { resetTableColors } from "../../../../../service/resetTableColors";
import { DATA_PRODUCER_TABLE_NAME } from "../../../../../service/producerServices/constants";
import { DATA_PRODUCER_WORKSHEET_NAME } from "../../../../../service/globalValues";
import { setStagingState } from "../../../../../service/store";
import { PricesResendManager } from "../../../../../classes/pricesResendManager";
import { LocalStorageKeys, SentryCustomData } from "../../../../../config";
import * as Sentry from "@sentry/react";

const ERROR_MESSAGE = "Invalid API token. Please check your token and try again.";
const SUCCESS_MESSAGE = "Connected";

enum FieldValidationState {
  SUCCESS = "success",
  ERROR = "error",
  NONE = "none",
}

interface InitializeCicadaArgs {
  apiKey: string;
  data: ProcessedData;
}

const TokenForm = () => {
  const [tokenVisible, setTokenVisible] = useState(false);
  const [apiKey, setApiKey] = useState("");

  const dispatch = useAppDispatch();

  const savedToken = useSelector(selectToken);
  const errorWhenValidatingToken = useSelector(selectIsErrorValidatingToken);
  const isLoading = useSelector(selectIsLoading);
  const isConnectionEstablished = useSelector(selectIsConnected);

  const styles = usePersonalAccessTokenStyles();

  const validationState = () => {
    if (errorWhenValidatingToken) return FieldValidationState.ERROR;

    if (isConnectionEstablished) return FieldValidationState.SUCCESS;

    return FieldValidationState.NONE;
  };

  const validationMessage = () => {
    if (errorWhenValidatingToken) return ERROR_MESSAGE;

    if (isConnectionEstablished) return SUCCESS_MESSAGE;

    return undefined;
  };

  const handleStart = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    localStorage.setItem(LocalStorageKeys.TOKEN, apiKey || savedToken);

    const data = await validateToken(dispatch(fetchData(apiKey || savedToken)));

    if (!data) return;

    const { token, data: userData } = data;

    await handleInitializeCicada({ apiKey: token, data: userData });
    resetTableColors({ table: DATA_PRODUCER_TABLE_NAME, worksheet: DATA_PRODUCER_WORKSHEET_NAME });
    dispatch(setStagingState({}));
    PricesResendManager.getInstance().resetManager();
    resetDataProducerButtonsText();

    Sentry.setExtra(SentryCustomData.CONNECTION_ESTABLISHED, true);
  };

  const handleInitializeCicada = async ({ apiKey, data }: InitializeCicadaArgs) => {
    try {
      await initializeCicada({ apiKey, data });

      dispatch(setConnected());

      if (apiKey) dispatch(setToken(apiKey));
    } catch (error) {
      const { message } = error as Error;

      dispatch(openToast({ type: "error", title: "Error", message }));
    }
  };

  const handleClearToken = () => {
    if (savedToken) dispatch(setDisconnected());

    if (isConnectionEstablished)
      dispatch(openToast({ type: "success", message: "Please reconnect to send quotes", title: "Disconnected" }));

    dispatch(setToken(null));
    setApiKey("");
    dispatch(setAutoUpdate(false));

    localStorage.setItem(LocalStorageKeys.TOKEN, null);

    Sentry.setExtra(SentryCustomData.CONNECTION_ESTABLISHED, false);
    Sentry.setExtra(SentryCustomData.AUTO_UPDATE_ENABLED, false);
  };

  return (
    <form role="form" onSubmit={handleStart} className={styles.root}>
      <Field
        validationState={validationState()}
        validationMessage={validationMessage()}
        required
        label={<Caption1>Enter your Personal Access Token provided by Cicada.</Caption1>}
      >
        <Input
          role="input"
          contentAfter={
            <button type="button" onClick={() => setTokenVisible(!tokenVisible)}>
              {tokenVisible ? <EyeOff16Regular /> : <Eye16Regular />}
            </button>
          }
          type={!tokenVisible ? "password" : "text"}
          value={savedToken || apiKey}
          onChange={(event) => !savedToken && setApiKey(event.target.value)}
        />
      </Field>

      <DropdownEnvironmentOptions isConnectionEstablished={isConnectionEstablished} />

      <div className={styles.buttonsContainer}>
        <Button size="small" onClick={handleClearToken} appearance="transparent">
          Clear Token
        </Button>
        <Button
          size="small"
          type="submit"
          icon={isLoading ? <Spinner size="extra-tiny" /> : <Link20Filled />}
          appearance="primary"
          disabled={isLoading || isConnectionEstablished}
        >
          Start
        </Button>
      </div>
    </form>
  );
};

export default TokenForm;
